'use client';

import { HelpCircle } from '@untitledui/icons';
import type { ReactNode, Ref } from 'react';
import type { LabelProps as AriaLabelProps } from 'react-aria-components/Label';
import { Label as AriaLabel } from 'react-aria-components/Label';

import { cx } from '../../../utils/cx';
import { Tooltip, TooltipTrigger } from '../tooltip/tooltip';

/**
 * @fileoverview Label component for the TRM UI Kit.
 * @agents generated no-review
 *
 * Form label component with support for tooltips and required indicators.
 * This component is generated from UntitledUI with TRM-specific customizations.
 *
 * @see https://www.untitledui.com/components
 */

interface LabelProps extends AriaLabelProps {
  children: ReactNode;
  isRequired?: boolean;
  tooltip?: string;
  tooltipDescription?: string;
  ref?: Ref<HTMLLabelElement>;
}

export const Label = ({
  isRequired,
  tooltip,
  tooltipDescription,
  className,
  ...props
}: LabelProps) => {
  return (
    <AriaLabel
      // Used for conditionally hiding/showing the label element via CSS:
      // <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
      // or
      // <Input label="Visible only on mobile" className="lg:label:hidden" />
      data-label="true"
      {...props}
      className={cx(
        'flex cursor-default items-center gap-0.5 text-sm font-medium text-secondary',
        className,
      )}
    >
      {props.children}

      <span
        className={cx(
          'hidden text-brand-tertiary',
          isRequired && 'block',
          typeof isRequired === 'undefined' && 'group-required:block',
        )}
      >
        *
      </span>

      {tooltip && (
        <Tooltip title={tooltip} description={tooltipDescription} placement="top">
          <TooltipTrigger
            // `TooltipTrigger` inherits the disabled state from the parent form field
            // but we don't that. We want the tooltip be enabled even if the parent
            // field is disabled.
            isDisabled={false}
            className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
          >
            <HelpCircle className="size-4" />
          </TooltipTrigger>
        </Tooltip>
      )}
    </AriaLabel>
  );
};

Label.displayName = 'Label';
